import { FormProvider, getFormProps } from '@conform-to/react'
import { Button, Flex } from '@radix-ui/themes'
import { useLocation } from '@remix-run/react'
import * as React from 'react'
import { RxPlus } from 'react-icons/rx'
import { z } from 'zod'
import { FormDefaults } from '#app/components/common/form-defaults.tsx'
import { SelectCountryCode } from '#app/components/common/select-country-code.tsx'
import { SubmissionButton } from '#app/components/common/submission-button.tsx'
import { Checkbox } from '#app/components/ui/checkbox.tsx'
import { TextField } from '#app/components/ui/text-field.tsx'
import { type Locale } from '#app/models/locale.ts'
import { type FormEnum } from '#app/routes/_layout/route-config.ts'
import { schemaDefault } from '#app/utils/form-defaults.ts'
import { useCreateAddressForm } from '#app/utils/hooks/forms/use-create-address-form.ts'

/* -------------------------------------------------------------------------------------------------
 * Create address form
 * -----------------------------------------------------------------------------------------------*/

enum TEST_IDS {}

enum INPUTS {
	firstName = 'firstName',
	lastName = 'lastName',
	phoneNumber = 'phoneNumber',
	company = 'company',
	street = 'street',
	city = 'city',
	postcode = 'postcode',
	countryCode = 'countryCode',
	defaultAddress = 'defaultAddress',
}

const FORM: FormEnum = {
	id: 'create-address-form',
	action: '/profile/addresses/create',
	fetcherKey: 'create-address',
	subaction: 'create-address',
	redirect: '/profile/addresses',
} as const

const schema = z.object({
	...schemaDefault,
	...{
		[INPUTS.firstName]: z
			.string({ required_error: 'validation.firstName.required' })
			.min(2, 'validation.firstName.min')
			.max(255, 'validation.firstName.max'),
		[INPUTS.lastName]: z
			.string({ required_error: 'validation.lastName.required' })
			.min(2, 'validation.lastName.min')
			.max(255, 'validation.lastName.max'),
		[INPUTS.phoneNumber]: z
			.string()
			.min(2, 'validation.phoneNumber.min')
			.max(255, 'validation.phoneNumber.max')
			.optional(),
		[INPUTS.company]: z
			.string()
			.min(2, 'validation.company.min')
			.max(255, 'validation.company.max')
			.optional(),
		[INPUTS.street]: z
			.string({ required_error: 'validation.street.required' })
			.min(2, 'validation.street.min')
			.max(255, 'validation.street.max'),
		[INPUTS.city]: z
			.string({ required_error: 'validation.city.required' })
			.min(2, 'validation.city.min')
			.max(255, 'validation.city.max'),
		[INPUTS.postcode]: z
			.string({ required_error: 'validation.postcode.required' })
			.min(2, 'validation.postcode.min')
			.max(255, 'validation.postcode.max'),
		[INPUTS.countryCode]: z.string({
			required_error: 'validation.countryCode.required',
		}),
		[INPUTS.defaultAddress]: z.boolean().optional(),
	},
})

type CreateAddressFormProps = {
	locale: Locale
}

const CreateAddressForm: React.FC<CreateAddressFormProps> = ({ locale }) => {
	const {
		createAddressFetcher,
		createAddressFormValues,
		createAddressForm,
		createAddressFields,
		t,
	} = useCreateAddressForm()

	const location = useLocation()

	return (
		<FormProvider context={createAddressForm.context}>
			<createAddressFetcher.Form
				method="post"
				action={`/${locale}${FORM.action}${location.search}`}
				{...getFormProps(createAddressForm)}
			>
				<FormDefaults
					form={createAddressForm}
					fields={createAddressFields}
					values={createAddressFormValues}
				/>
				<Flex direction="column" gapY="5" mt="2">
					{[
						{ title: INPUTS.firstName },
						{ title: INPUTS.lastName },
						{ title: INPUTS.phoneNumber, type: 'tel' },
						{ title: INPUTS.company },
						{ title: INPUTS.street },
						{ title: INPUTS.city },
						{ title: INPUTS.postcode, type: 'tel' },
					].map(({ title, type = 'text' }) => (
						<TextField
							key={title}
							type={type}
							meta={createAddressFields[title]}
							t={t}
						>
							{t(`label.${title}`)}
						</TextField>
					))}
					<SelectCountryCode
						meta={createAddressFields[INPUTS.countryCode]}
						t={t}
						placeholder="???????????"
					>
						???????????
					</SelectCountryCode>
					<Checkbox t={t} meta={createAddressFields[INPUTS.defaultAddress]}>
						{t('button.setAsDefaultAddress')}
					</Checkbox>
					<Button size="4" type="submit">
						<SubmissionButton fetcher={createAddressFetcher}>
							<RxPlus size="1.2em" />
							{t('button.createAddressSubmit')}
						</SubmissionButton>
					</Button>
				</Flex>
			</createAddressFetcher.Form>
		</FormProvider>
	)
}
CreateAddressForm.displayName = 'CreateAddressForm'

/* -----------------------------------------------------------------------------------------------*/

export {
	type CreateAddressFormProps,
	CreateAddressForm,
	TEST_IDS as CREATE_ADDRESS_TEST_IDS,
	INPUTS as CREATE_ADDRESS_INPUTS,
	FORM as CREATE_ADDRESS_FORM,
	schema as createAddressSchema,
}
